import type {
  PlayerAdapter,
  PlayerInitPayload,
  PlayerApplyResult,
  PlayerCapabilities,
  ConfigurationState,
  ConfigAction,
  SetAttributeValuePayload,
  VisualTarget,
  SnapshotOptions,
  SnapshotResult,
  ValidationIssue,
} from '@config3d/contracts';
import { DEFAULT_CAPABILITIES } from '@config3d/contracts';

export interface SceneStoreBridge {
  setSelection(key: string, value: string): void;
  clearSelections(): void;
  getSelections(): Record<string, string>;
}

export interface SceneViewerBridge {
  captureSnapshot(options: SnapshotOptions): Promise<string>;
  highlightNode(targetId: string): void;
  clearHighlight(): void;
}

export class Config3DPlayerAdapter implements PlayerAdapter {
  private store: SceneStoreBridge;
  private viewer: SceneViewerBridge;
  private initialized = false;

  constructor(store: SceneStoreBridge, viewer: SceneViewerBridge) {
    this.store = store;
    this.viewer = viewer;
  }

  async init(payload: PlayerInitPayload): Promise<{ capabilities: PlayerCapabilities }> {
    this.store.clearSelections();
    for (const [key, val] of Object.entries(payload.initialConfiguration.selections)) {
      if (typeof val === 'string') this.store.setSelection(key, val);
    }
    this.initialized = true;
    return { capabilities: { ...DEFAULT_CAPABILITIES } };
  }

  async applyConfiguration(state: ConfigurationState): Promise<PlayerApplyResult> {
    if (!this.initialized) return { accepted: false, issues: [notInit()] };
    for (const [key, val] of Object.entries(state.selections)) {
      if (typeof val === 'string') this.store.setSelection(key, val);
    }
    return { accepted: true };
  }

  async applyDelta(actions: ConfigAction[]): Promise<PlayerApplyResult> {
    if (!this.initialized) return { accepted: false, issues: [notInit()] };
    for (const action of actions) {
      if (action.type === 'setAttributeValue') {
        const p = action.payload as unknown as SetAttributeValuePayload;
        this.store.setSelection(p.attributeId, String(p.value));
      } else if (action.type === 'unsetAttributeValue') {
        const p = action.payload as { attributeId: string };
        this.store.setSelection(p.attributeId, '');
      } else if (action.type === 'resetConfiguration') {
        this.store.clearSelections();
      }
    }
    return { accepted: true };
  }

  highlightTarget(target: VisualTarget): void {
    this.viewer.highlightNode(target.targetId);
  }

  setView(_view: { cameraId?: string; animationId?: string }): void {
    // Phase 2: camera presets
  }

  async requestSnapshot(options: SnapshotOptions): Promise<SnapshotResult> {
    const dataUrl = await this.viewer.captureSnapshot(options);
    return { snapshotId: `snap_${Date.now()}`, url: dataUrl };
  }

  destroy(): void {
    this.initialized = false;
  }
}

function notInit(): ValidationIssue {
  return { code: 'PLAYER_NOT_INITIALIZED', message: 'Call init() first.', severity: 'error' };
}
